import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const TelegramBetsView = () => {
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const [systemStatus, setSystemStatus] = useState(null);

  const loadTelegramBets = async () => {
    try {
      setLoading(true);
      
      // Try the API endpoint first
      const response = await axios.get(`${API_BASE_URL}/telegram/bets/today`);
      console.log('API Response:', response.data);
      
      const betsData = response.data.raw_bets || [];
      setBets(betsData);
      
      // If no bets from API, try direct database query like telegram bot
      if (betsData.length === 0) {
        console.log('No bets from API, trying direct database query...');
        try {
          const directResponse = await axios.get(`${API_BASE_URL}/bets/daily`);
          console.log('Direct DB Response:', directResponse.data);
          
          if (directResponse.data && directResponse.data.length > 0) {
            setBets(directResponse.data);
          } else {
            // Try getting all bets for debugging
            console.log('No daily bets found, checking all bets...');
            const allBetsResponse = await axios.get(`${API_BASE_URL}/bets/daily/all`);
            console.log('All bets in daily_bets collection:', allBetsResponse.data);
            setBets(allBetsResponse.data || []);
          }
        } catch (directErr) {
          console.error('Direct query failed:', directErr);
        }
      }
      
      // Load system status
      const statusResponse = await axios.get(`${API_BASE_URL}/telegram/system/status`);
      setSystemStatus(statusResponse.data);
    } catch (err) {
      console.error('Error loading telegram bets:', err);
      setBets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTelegramBets();
  }, [filter]);

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-blue-100 text-blue-800',
      won: 'bg-green-100 text-green-800',
      lost: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const totalAmount = bets.reduce((sum, bet) => sum + bet.amount, 0);
  const activeBets = bets.filter(bet => bet.status === 'active').length;
  
  // Group bets by lucky number for display
  const groupedBets = bets.reduce((groups, bet) => {
    if (!groups[bet.lucky_number]) {
      groups[bet.lucky_number] = [];
    }
    groups[bet.lucky_number].push(bet);
    return groups;
  }, {});

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">📱 Telegram Lucky Draw Management</h2>
        <div className="flex gap-4">
          <div className="bg-blue-50 px-4 py-2 rounded-lg">
            <span className="text-sm text-blue-600">Total Bets</span>
            <div className="text-xl font-bold text-blue-800">{bets.length}</div>
          </div>
          <div className="bg-green-50 px-4 py-2 rounded-lg">
            <span className="text-sm text-green-600">Unique Numbers</span>
            <div className="text-xl font-bold text-green-800">{Object.keys(groupedBets).length}</div>
          </div>
          <div className="bg-purple-50 px-4 py-2 rounded-lg">
            <span className="text-sm text-purple-600">Total Points</span>
            <div className="text-xl font-bold text-purple-800">{totalAmount}</div>
          </div>
          <div className={`px-4 py-2 rounded-lg ${
            systemStatus?.betting_allowed ? 'bg-green-50' : 'bg-red-50'
          }`}>
            <span className={`text-sm ${
              systemStatus?.betting_allowed ? 'text-green-600' : 'text-red-600'
            }`}>Betting Status</span>
            <div className={`text-xl font-bold ${
              systemStatus?.betting_allowed ? 'text-green-800' : 'text-red-800'
            }`}>
              {systemStatus?.betting_allowed ? 'Open' : 'Closed'}
            </div>
          </div>
        </div>
      </div>

      {/* Info Message */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-bold text-blue-800 mb-2">🏆 Prize Configuration</h3>
        <p className="text-blue-700">
          To select winners and set prize points, go to the <strong>Draw Cycle</strong> tab.
          Prize structure must be configured before 9 PM Malaysian Time.
        </p>
        {systemStatus && (
          <div className="mt-3 text-sm text-blue-600">
            <strong>Current Status:</strong> {systemStatus.current_time} | 
            Betting: {systemStatus.betting_allowed ? '🟢 Open' : '🔴 Closed'}
          </div>
        )}
      </div>



      {/* Bets Table */}
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading telegram bets...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lucky Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bets.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center">
                      <div className="text-gray-500">
                        <div className="text-lg font-medium mb-2">📱 No Telegram Bets Found</div>
                        <div className="text-sm">
                          {systemStatus?.betting_allowed 
                            ? "Users haven't placed any bets yet today. Betting is currently open."
                            : "Betting is closed. Check back tomorrow or after 6 AM Malaysian time."
                          }
                        </div>
                        <div className="mt-2 text-xs text-gray-400">
                          Current Malaysian Time: {systemStatus?.current_time || 'Loading...'}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  bets.map((bet) => (
                    <tr key={bet._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            📱 {bet.customer_number || bet.user_phone || bet.phone || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {bet.user_id || bet.telegram_id || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-400">
                            Bet Date: {bet.bet_date || 'N/A'} | Created: {bet.created_at ? new Date(bet.created_at).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-lg font-bold text-blue-600">
                          {bet.lucky_number}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          ₹{bet.amount}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(bet.status)}`}>
                          {bet.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(bet.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {bet.status === 'active' && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => updateBetStatus(bet._id, 'won')}
                              className="text-green-600 hover:text-green-900 px-2 py-1 rounded bg-green-50 hover:bg-green-100"
                            >
                              Mark Won
                            </button>
                            <button
                              onClick={() => updateBetStatus(bet._id, 'lost')}
                              className="text-red-600 hover:text-red-900 px-2 py-1 rounded bg-red-50 hover:bg-red-100"
                            >
                              Mark Lost
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  async function updateBetStatus(betId, status) {
    try {
      await axios.put(`${API_BASE_URL}/bets/${betId}`, { status });
      loadTelegramBets(); // Reload the data
    } catch (err) {
      console.error('Error updating bet status:', err);
      alert('Failed to update bet status');
    }
  }
};

export default TelegramBetsView;